import { useCallback, useMemo } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  LoginPhoneChallenge,
  isLoginPhoneChallenge,
} from '@shared/api/auth/loginPhoneVerification.controller';
import { User } from '@shared/api/user/user.interface';
import { queryString } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { CamelRole, LowerRole, UserRole } from '@shared/types';
import { withoutLoginDeviceId } from '@shared/utils/loginDevice.util';
import {
  isLoginDeviceLocked,
  loginErrorMessage,
  passwordCheckErrorMessage,
  passwordSetErrorMessage,
} from '@shared/utils/loginError.util';
import { AxiosError } from 'axios';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';

import { tokenSelector, userProfileSelector } from '../atom/auth.atom';
import { userBugoBrandSelector } from './../atom/auth.atom';

/** 운영 계정이 카카오 등 다른 경로로 로그인을 시도했을 때의 안내 */
const ADMIN_NEEDS_PASSWORD_LOGIN =
  '관리자 계정은 아이디·비밀번호로 로그인한 뒤 휴대폰 인증을 진행해 주세요.';

/** 현재 비밀번호 확인 결과. 실패 사유(남은 횟수·기기 잠금)를 함께 돌려준다 */
export interface PasswordVerifyResult {
  verified: boolean;
  message: string | null;
  /** 이 기기가 잠긴 상태 — 다시 입력해도 소용없다 */
  locked: boolean;
}

export interface GuestDto {
  name: string;
  phoneNumber: string;
  password: string;
}

export const useAuth = () => {
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const [token, setToken] = useRecoilState(tokenSelector);

  //* authenticated 여부
  const guestAuthenticated = useMemo(() => {
    return !_.isNil(token);
  }, [token]);

  const [userProfile, setUserProfile] = useRecoilState(userProfileSelector);

  const userBugoBrand = useRecoilValue(userBugoBrandSelector);

  const userHeaderImg = userBugoBrand?.bugoAgency?.headerImage;

  const userBottomImg = userBugoBrand?.bugoAgency?.footerImage;

  const isAuthenticated = guestAuthenticated;

  /**
   * 로그인·가입 실패 안내.
   *
   * 문구 판단은 loginError.util 이 한다 — 실패 갈래가 셋(401 남은 횟수 / 429 기기 잠금 /
   * 403 사유 문장)으로 늘어서 상태코드 switch 로는 부족해졌다.
   */
  const notificationGenerator = useCallback(
    (errObj: AxiosError) => {
      notificateError(
        notifiacationInstance,
        loginErrorMessage(errObj),
        undefined,
        undefined,
        //* 잠금 안내는 시각·남은 시간이 붙어 길다. 기본 1.5초로는 못 읽는다
        isLoginDeviceLocked(errObj) ? 8 : undefined,
      );
    },
    [notifiacationInstance],
  );
  class DecodedToken {
    roles: UserRole[];
    _id: string;
    constructor(public token: string | null) {
      if (!_.isNil(token)) {
        const { sub, roles, exp } = jwtDecode(token) as any;
        const cTime = new Date().getTime() / 1000;

        if (cTime >= exp) {
          localStorage.removeItem('token');
          window.location.reload();

          this._id = 'unexpected';
          this.roles = [];
          return;
        }

        this._id = sub;
        this.roles = roles;
      } else {
        this._id = 'unexpected';
        this.roles = [];
      }
    }
  }

  const dtoken = new DecodedToken(token);

  /** =======================================================
   * signup
   * ======================================================== */

  const signupByUsernamePasswordCheckPossible = async ({
    username,
  }: {
    username: string;
  }) => {
    const {
      data: { token },
    } = await api_.post('/auth/signup/username-password/possible', {
      username,
    });
    if (token) setToken(token);
    const profile = await getMe(token);
    if (profile) {
      setUserProfile(profile);
    }
    return token;
  };

  const signupByUsernamePassword = async ({
    username,
    password,
    roles,
  }: {
    username: string;
    password: string;
    roles: UserRole[];
  }) => {
    try {
      const res = await api_.post('/auth/signup/username-password', {
        username,
        password,
        roles,
      });
      if (res.status !== 200) {
        notificateError(notifiacationInstance, res.data);
      }
      const {
        data: { token },
      } = res;
      console.log(res);
      if (token) setToken(token);
      const profile = await getMe(token);
      if (profile) {
        setUserProfile(profile);
      }
      return token;
    } catch (err) {
      console.error(err);
    }
  };

  const signupByKakaoCheckPossible = async ({ code }: { code: string }) => {
    const {
      data: { token },
    } = await api_.post('/auth/signup/social/kakao/possible', { code });
    if (token) setToken(token);
    const profile = await getMe(token);
    if (profile) {
      setUserProfile(profile);
    }
    return token;
  };

  //TODO: roles 입력받도록 (user info 입력도 받도록)
  const signupByKakao = async ({ code, roles }: { code: string; roles: UserRole[] }) => {
    const {
      data: { token },
    } = await api_.post('/auth/signup/social/kakao', {
      code,
      roles,
    });
    if (token) setToken(token);
    const profile = await getMe(token);
    if (profile) {
      setUserProfile(profile);
    }
    return token;
  };

  /** =======================================================
   * login
   * ======================================================== */

  /**
   * 토큰을 받은 뒤의 공통 마무리 — 세션 저장, 프로필 조회, 접속 브랜드 검증.
   * 아이디·비밀번호 로그인과 휴대폰 2단계 인증(completeLoginWithToken)이 같이 쓴다.
   */
  const finalizeLogin = async (token: string | undefined) => {
    if (token) setToken(token);
    const profile = await getMe(token);

    // bugoBrand 검증 로직
    const forceBugoBrandId = process.env.REACT_APP_FORCE_LOGIN_BUGO_BRAND_ID;
    if (forceBugoBrandId && profile) {
      const hasAgencyRole =
        profile.roles?.includes(UserRole.BugoAgencyWorker) ||
        profile.roles?.includes(UserRole.BugoAgencyAdmin);

      if (hasAgencyRole) {
        // bugoBrand가 string이면 직접 비교, object이면 _id 비교
        const userBugoBrandId =
          typeof profile.bugoBrand === 'string'
            ? profile.bugoBrand
            : (profile.bugoBrand as any)?._id;

        if (userBugoBrandId !== forceBugoBrandId) {
          // 로그인 거부 - token과 profile 초기화
          setToken(null);
          setUserProfile(null);
          notificateError(
            notifiacationInstance,
            `해당 사이트에 접속할 권한이 없는 계정입니다.`,
          );
          throw new Error('BUGO_BRAND_ACCESS_DENIED');
        }
      }
    }

    if (profile) {
      setUserProfile(profile);
    }
    notificateSuccess(notifiacationInstance, '로그인 되었습니다');

    //* 취약 비밀번호 안내는 로그인 직후 팝업으로 띄우지 않는다 — 놀라서 전화가 온다.
    //* 사이드바 계정 링크 옆 배지(WeakPasswordBadge)와 계정정보 화면 안내로만 조용히 유도한다.

    return token;
  };

  /** 로그인 실패 안내. 접속 브랜드 거부는 이미 자기 안내를 띄웠으므로 건너뛴다 */
  const handleLoginError = (err: unknown) => {
    if ((err as Error)?.message === 'BUGO_BRAND_ACCESS_DENIED') return;
    notificationGenerator(err as AxiosError);
  };

  /**
   * 아이디·비밀번호 로그인.
   *
   * 운영 계정(Admin)은 비밀번호가 맞아도 토큰이 나가지 않고 휴대폰 인증 challenge가 온다.
   * 반환값이 LoginPhoneChallenge면 아직 로그인 전이므로 인증 화면으로 넘겨야 한다
   * (isLoginPhoneChallenge로 판별).
   */
  const loginByUsernamePassword = async (
    data: {
      username: string;
      password: string;
    },
    options?: {
      /**
       * 고객 화면(화환 스토어 로그인 등)에서의 호출. 기기 헤더를 빼서 서버가 실패 횟수 잠금을
       * 세지도 걸지도 않게 한다 — 고객은 어떤 경우에도 잠기면 안 된다. 콘솔 로그인은 기본(false).
       */
      customerSurface?: boolean;
    },
  ): Promise<string | LoginPhoneChallenge | undefined> => {
    //* 실패 안내는 여기서 띄우지 않는다 — 유일한 호출부인 LoginForm 이 폼 아래 카드로
    //* 남은 횟수·잠금 카운트다운을 그린다 (parseLoginFailure). 토스트를 겹치면 두 번 뜬다.
    const res = await api_.post(
      '/auth/login/username-password',
      data,
      ...(options?.customerSurface ? [withoutLoginDeviceId()] : []),
    );

    //* 토큰이 없는 200 — 휴대폰 인증을 마쳐야 토큰이 나온다
    if (isLoginPhoneChallenge(res.data)) return res.data;

    try {
      return await finalizeLogin(res.data?.token);
    } catch (err) {
      //* 토큰은 받았는데 프로필 조회·브랜드 검증에서 실패한 경우만 여기서 안내
      handleLoginError(err);
      throw err;
    }
  };

  /** 휴대폰 2단계 인증까지 마치고 받은 토큰으로 로그인을 완료한다 */
  const completeLoginWithToken = async (token: string) => {
    try {
      return await finalizeLogin(token);
    } catch (err) {
      handleLoginError(err);
      throw err;
    }
  };

  const loginByKakao = async (data: any) => {
    const res = await api_.post('/auth/login/social/kakao', data);
    //* 운영 계정은 카카오로 우회 로그인되지 않는다 (백엔드가 같은 관문을 건다)
    if (isLoginPhoneChallenge(res.data)) throw new Error(ADMIN_NEEDS_PASSWORD_LOGIN);
    const {
      data: { token },
    } = res;
    if (token) setToken(token);
    const profile = await getMe(token);
    if (profile) {
      setUserProfile(profile);
    }
    return token;
  };

  const loginStoreGuest = async (data: any) => {
    try {
      const {
        data: { token },
      } = await api_.post('/auth/login/guest/store', data);
      if (token) setToken(token);
      const profile = await getMe(token);
      if (profile) {
        setUserProfile(profile);
      }

      notificateSuccess(notifiacationInstance, '로그인 되었습니다');
      return token;
    } catch (err) {
      const errObj = err as AxiosError;
      notificationGenerator(errObj);
      throw err;
    }
  };

  const loginBugoGuest = async (data: any) => {
    try {
      const {
        data: { token },
      } = await api_.post('/auth/login/guest/bugo', data);
      if (token) setToken(token);
      const profile = await getMe(token);
      if (profile) {
        setUserProfile(profile);
      }
      notificateSuccess(notifiacationInstance, '로그인 되었습니다');
      return token;
    } catch (err) {
      const errObj = err as AxiosError;
      notificationGenerator(errObj);
      throw err;
    }
  };

  /** =======================================================
   * signup-or-login
   * ======================================================== */
  const signupOrLoginByKakao = async ({
    code,
    roles,
  }: {
    code: string;
    roles: UserRole[];
  }) => {
    try {
      const res = await api_.post('/auth/signup-or-login/social/kakao', {
        code,
        roles,
      });
      //* 운영 계정은 카카오로 우회 로그인되지 않는다 (백엔드가 같은 관문을 건다)
      if (isLoginPhoneChallenge(res.data)) throw new Error(ADMIN_NEEDS_PASSWORD_LOGIN);
      const {
        data: { token },
      } = res;
      if (token) setToken(token);
      const profile = await getMe(token);
      if (profile) {
        setUserProfile(profile);
      }
      notificateSuccess(notifiacationInstance, '로그인 되었습니다');
      return token;
    } catch (err) {
      if ((err as Error)?.message === ADMIN_NEEDS_PASSWORD_LOGIN) {
        notificateError(notifiacationInstance, ADMIN_NEEDS_PASSWORD_LOGIN);
      } else {
        notificationGenerator(err as AxiosError);
      }
      throw err;
    }
  };

  const signupOrLoginStoreGuest = async (
    data: {
      name: string;
      phoneNumber: string;
      password: string;
    },
    silence?: boolean,
  ) => {
    try {
      const {
        data: { token },
      } = await api_.post('/auth/signup-or-login/guest/store', data);
      if (token) setToken(token);
      const profile = await getMe(token);
      if (profile) {
        setUserProfile(profile);
      }
      if (silence) {
        return token;
      }
      notificateSuccess(notifiacationInstance, '로그인 되었습니다');
      return token;
    } catch (err) {
      const errObj = err as AxiosError;
      notificationGenerator(errObj);
      throw err;
    }
  };

  /**
   * 로그인은 되어 있으나 StoreCustomer가 아닌 유저(Admin/StoreSeller/Bugo* 등)를
   * 주문 직전에 StoreCustomer로 additive 승격시킨다.
   * - 백엔드는 기존 role을 보존한 채 StoreCustomer만 추가($addToSet)하고 새 토큰을 반환
   * - 멱등하므로 이미 StoreCustomer여도 호출 안전(토큰만 갱신됨)
   * - 토큰 교체 후 getMe로 userProfile.roles를 갱신해야 storeRole()이 store-customer로 바뀜
   */
  const ensureStoreCustomer = async (silence?: boolean) => {
    try {
      const {
        data: { token },
      } = await api_.patch('/auth/ensure/store-customer');
      if (token) setToken(token);
      const profile = await getMe(token);
      if (profile) {
        setUserProfile(profile);
      }
      if (!silence) {
        notificateSuccess(notifiacationInstance, '구매 회원으로 전환되었습니다');
      }
      return token;
    } catch (err) {
      const errObj = err as AxiosError;
      notificationGenerator(errObj);
      throw err;
    }
  };

  const signupOrLoginBugoGuest = async (data: {
    name: string;
    phoneNumber: string;
    password: string;
  }) => {
    try {
      const {
        data: { token },
      } = await api_.post('/auth/signup-or-login/guest/bugo', data);
      if (token) setToken(token);
      const profile = await getMe(token);
      if (profile) {
        setUserProfile(profile);
      }
      notificateSuccess(notifiacationInstance, '로그인 되었습니다');
      return token;
    } catch (err) {
      const errObj = err as AxiosError;
      notificationGenerator(errObj);
      throw err;
    }
  };

  /** =======================================================
   * user api
   * ======================================================== */
  const linkByKakao = async (dto: { code: string }) => {
    if (!token) return;
    try {
      const { data } = await api_.patch<User>(`/auth/link/social/kakao`, dto);
      return data;
    } catch (error: any) {
      console.error(error);
      console.error(error?.response?.data);
    }
  };

  const getMe = async (token: string | undefined) => {
    if (!token) return;
    try {
      const { data } = await api_.get<User>(
        `auth/me/?${queryString({
          populate: {
            path: 'bugoAgencyAdminDetail.bugoBrands',
          },
        })}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      console.error(error?.response?.data);
    }
  };

  const patchMe = async (dto: Pick<User, 'info' | 'adminDetail'>) => {
    if (!token) return;
    try {
      const { data } = await api_.patch<User>(`/auth/me/detail`, dto);
      return data;
    } catch (error: any) {
      console.error(error);
      console.error(error?.response?.data);
    }
  };

  //! 비밀번호 변경
  /**
   * 비밀번호 변경. 현재 비밀번호 또는 휴대폰 인증 증명(proof)을 항상 같이 보낸다 —
   * 지금 서버는 password 만 읽지만(검증 없음), 검증을 켜도 그대로 통과하도록 미리 동봉한다.
   * 요청 스펙: smartbugo-api/docs/me-password-phone-verification-spec.md
   */
  const patchMePassword = async (
    newPassword: string,
    proof?: { currentPassword?: string; phoneProof?: string },
  ): Promise<boolean> => {
    try {
      await api_.patch('/auth/me/password', {
        password: newPassword,
        ...(proof?.currentPassword ? { currentPassword: proof.currentPassword } : {}),
        ...(proof?.phoneProof ? { phoneProof: proof.phoneProof } : {}),
      });
      notificateSuccess(notifiacationInstance, '비밀번호 변경이 완료되었습니다.');
      // await logout();
      return true;
    } catch (err) {
      //* 취약 비밀번호(400 PASSWORD_TOO_WEAK)는 서버 문장을 그대로 — 기준이 등급별이라 여기서 재현하지 않는다
      notificateError(
        notifiacationInstance,
        passwordSetErrorMessage(err, '비밀번호 변경에 실패했습니다.'),
        undefined,
        undefined,
        4,
      );
      console.error(err);
      return false;
    }
  };

  /**
   * 현재 비밀번호 검증 (로그인 엔드포인트 재호출, 세션 부수효과 없음).
   *
   * 운영 계정(Admin)이 기기 인증이 만료된 상태면 토큰 대신 휴대폰 인증 challenge가 온다.
   * 그 관문은 비밀번호를 대조한 뒤에만 돌기 때문에 challenge가 왔다는 건 **비밀번호가 맞았다**는
   * 뜻이다 — 토큰 유무만 보면 "비밀번호가 틀렸다"로 잘못 판정하게 된다.
   *
   * 로그인 엔드포인트를 그대로 쓰므로 **실패는 기기 잠금 횟수에 쌓인다.**
   * 그래서 boolean이 아니라 사유를 함께 돌려준다 — 남은 횟수를 알려주지 않으면
   * 비밀번호 변경 화면에서 몇 번 틀린 관리자가 영문도 모르고 로그인까지 잠긴다.
   */
  const verifyCurrentPassword = async (
    currentPassword: string,
  ): Promise<PasswordVerifyResult> => {
    if (!userProfile?.username) {
      return { verified: false, message: null, locked: false };
    }
    try {
      const { data } = await api_.post('/auth/login/username-password', {
        username: userProfile.username,
        password: currentPassword,
      });
      const verified = Boolean(data?.token) || isLoginPhoneChallenge(data);
      return {
        verified,
        message: verified ? null : '현재 비밀번호가 일치하지 않습니다.',
        locked: false,
      };
    } catch (err) {
      return {
        verified: false,
        message: passwordCheckErrorMessage(err),
        locked: isLoginDeviceLocked(err),
      };
    }
  };

  const refreshUserProfile = async () => {
    if (token) {
      const userProfile = await getMe(token);
      if (userProfile) {
        setUserProfile(userProfile);
      } else {
        // console.log('redirect 해야함');
      }
    }
  };

  /** =======================================================
   * link
   * ======================================================== */

  /** =======================================================
   * utils
   * ======================================================== */
  const bugoBrandId =
    userProfile?.bugoAgencyAdminDetail?.bugoBrands &&
    userProfile.bugoAgencyAdminDetail.bugoBrands.length > 0
      ? userProfile?.bugoAgencyAdminDetail?.bugoBrands[0]?._id ??
        'agencyAdminHasNoBugoBrand'
      : userProfile?.bugoBrand ?? 'workerHasNoBugoBrand';

  const roles = userProfile?.roles;

  const isAdmin = () => {
    if (roles) {
      return roles.includes(UserRole.Admin);
    } else {
      return false;
    }
  };

  const isBugoAgencyAdmin = () => {
    if (roles) {
      return roles.includes(UserRole.BugoAgencyAdmin);
    } else {
      return false;
    }
  };

  const isBugoAgencyBranchAdmin = () => {
    if (roles) {
      return roles.includes(UserRole.BugoAgencyBranchAdmin);
    } else {
      return false;
    }
  };

  const isBugoAgencyWorker = () => {
    if (roles) {
      return roles.includes(UserRole.BugoAgencyWorker);
    } else {
      return false;
    }
  };

  const isBugoFuneralHome = () => {
    if (roles) {
      return roles.includes(UserRole.BugoFuneralHome);
    } else {
      return false;
    }
  };

  const isBugoCustomer = () => {
    if (roles) {
      return roles.includes(UserRole.BugoCustomer);
    } else {
      return false;
    }
  };

  const isBugoGuest = () => {
    if (roles) {
      return roles.includes(UserRole.BugoGuest);
    } else {
      return false;
    }
  };

  const isStoreSeller = () => {
    if (roles) {
      return (
        roles.includes(UserRole.StoreSeller) ||
        roles.includes(UserRole.StoreSellerAdmin) ||
        roles.includes(UserRole.StoreSellerWorker)
      );
    } else {
      return false;
    }
  };

  const isGiftAgent = () => {
    if (roles) {
      return roles.includes(UserRole.GiftAgent);
    } else {
      return false;
    }
  };

  const isGiftBrandSeller = () => {
    if (roles) {
      return roles.includes(UserRole.GiftBrandSeller);
    } else {
      return false;
    }
  };

  const assetRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin)) {
        return LowerRole.Admin;
      } else {
        return LowerRole.NonAdmin;
      }
    } else {
      return LowerRole.NonAdmin;
    }
  };

  const bugoRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin)) {
        return LowerRole.Admin;
      } else if (roles.includes(UserRole.BugoAgencyAdmin)) {
        return LowerRole.BugoAgencyAdmin;
      } else if (roles.includes(UserRole.BugoAgencyBranchAdmin)) {
        return LowerRole.BugoAgencyBranchAdmin;
      } else if (roles.includes(UserRole.BugoAgencyWorker)) {
        return LowerRole.BugoAgencyWorker;
      } else if (roles.includes(UserRole.BugoFuneralHome)) {
        return LowerRole.BugoFuneralHome;
      } else if (roles.includes(UserRole.BugoCustomer)) {
        return LowerRole.BugoCustomer;
      } else if (roles.includes(UserRole.BugoGuest)) {
        return LowerRole.BugoGuest;
      } else return LowerRole.Public;
    } else {
      return LowerRole.Public;
    }
  };

  const bugoWorkerRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin)) {
        return LowerRole.Admin;
      } else if (roles.includes(UserRole.BugoAgencyWorker)) {
        return LowerRole.BugoAgencyWorker;
      } else return LowerRole.Public;
    } else {
      return LowerRole.Public;
    }
  };

  const storeRole = () => {
    if (roles) {
      if (roles.includes(UserRole.StoreCustomer)) {
        return LowerRole.StoreCustomer;
      } else if (roles.includes(UserRole.StoreGuest)) {
        return LowerRole.StoreGuest;
      } else return LowerRole.Public;
    } else {
      return LowerRole.Public;
    }
  };

  const sellerRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin)) {
        return LowerRole.Admin;
      } else if (
        roles.includes(UserRole.StoreSeller) ||
        roles.includes(UserRole.StoreSellerAdmin) ||
        roles.includes(UserRole.StoreSellerWorker)
      ) {
        return LowerRole.StoreSeller;
      } else return LowerRole.Public;
    } else {
      return LowerRole.Public;
    }
  };

  // 답례품 배송 포털 role: Admin/GiftAgent는 전 브랜드(admin), 그 외는 본인 브랜드(gift-brand-seller)
  const giftBrandRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin) || roles.includes(UserRole.GiftAgent)) {
        return LowerRole.Admin;
      } else if (roles.includes(UserRole.GiftBrandSeller)) {
        return LowerRole.GiftBrandSeller;
      } else return LowerRole.Public;
    } else {
      return LowerRole.Public;
    }
  };

  const bugoCamelRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin)) {
        return CamelRole.Admin;
      } else if (roles.includes(UserRole.BugoAgencyAdmin)) {
        return CamelRole.BugoAgencyAdmin;
      } else if (roles.includes(UserRole.BugoAgencyWorker)) {
        return CamelRole.BugoAgencyWorker;
      } else if (roles.includes(UserRole.BugoFuneralHome)) {
        return CamelRole.BugoFuneralHome;
      } else if (roles.includes(UserRole.BugoCustomer)) {
        return CamelRole.BugoCustomer;
      } else if (roles.includes(UserRole.BugoGuest)) {
        return CamelRole.BugoGuest;
      } else return CamelRole.BugoGuest;
    } else {
      return CamelRole.BugoGuest;
    }
  };

  const storeCamelRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin)) {
        return CamelRole.Admin;
      } else if (roles.includes(UserRole.StoreCustomer)) {
        return CamelRole.StoreCustomer;
      } else if (roles.includes(UserRole.StoreGuest)) {
        return CamelRole.StoreGuest;
      } else return CamelRole.Public;
    }
  };

  const sellerCamelRole = () => {
    if (roles) {
      if (roles.includes(UserRole.Admin)) {
        return CamelRole.Admin;
      } else if (
        roles.includes(UserRole.StoreSeller) ||
        roles.includes(UserRole.StoreSellerAdmin) ||
        roles.includes(UserRole.StoreSellerWorker)
      ) {
        return CamelRole.StoreSeller;
      } else return CamelRole.Public;
    } else {
      return CamelRole.Public;
    }
  };

  const logout = async () => {
    setToken(null);
    setUserProfile(null);
    window.location.reload();
  };

  const apiLogout = async () => {
    setToken(null);
    setUserProfile(null);
    window.location.reload();
  };

  return {
    roles,
    token,
    guestAuthenticated,
    isAuthenticated,
    userProfile,
    userBugoBrand,
    bugoBrandId,
    signupByUsernamePasswordCheckPossible,
    signupByUsernamePassword,
    signupByKakaoCheckPossible,
    loginByUsernamePassword,
    completeLoginWithToken,
    loginByKakao,
    loginStoreGuest,
    loginBugoGuest,
    linkByKakao,
    patchMe,
    getMe,
    patchMePassword,
    verifyCurrentPassword,
    signupOrLoginStoreGuest,
    ensureStoreCustomer,
    signupOrLoginBugoGuest,
    signupOrLoginByKakao,
    signupByKakao,
    isAdmin,
    isBugoAgencyAdmin,
    isBugoAgencyWorker,
    isBugoAgencyBranchAdmin,
    isBugoFuneralHome,
    isBugoCustomer,
    isBugoGuest,
    isStoreSeller,
    isGiftAgent,
    isGiftBrandSeller,
    bugoRole,
    storeRole,
    sellerRole,
    giftBrandRole,
    bugoCamelRole,
    bugoWorkerRole,
    storeCamelRole,
    sellerCamelRole,
    assetRole,
    refreshUserProfile,
    logout,
    apiLogout,
    userHeaderImg,
    userBottomImg,
    dtoken,
  };
};
